// P2b-3 §5: navigator.clipboard wrapper with a textarea fallback for
// the older browsers + insecure-context (http://localhost on some setups)
// that lack the async API. Pure: no React, no DOM-globals usage outside
// the fallback's temporary <textarea>.
//
// Returns a boolean indicating whether the copy actually succeeded.
// Callers use the result to decide whether to flash "Copied!" or fall
// back to a manual-copy hint.

export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied / not allowed in this context. Fall through
      // to the document.execCommand path below.
    }
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Position offscreen but keep it focusable. `position: fixed` avoids
  // jumping the page if the user has scrolled.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-1000px';
  textarea.style.left = '-1000px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  let ok = false;
  try {
    // execCommand is deprecated but still works in every shipping
    // browser. The async API above is the preferred path; this is the
    // fallback.
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  } finally {
    document.body.removeChild(textarea);
  }
  return ok;
}
